import { useState } from 'react';
import api from '../services/api';

interface DataItem {
  category: string;
  table: string;
  count: number;
  description: string;
}

interface DeletionPreview {
  userId: string;
  userType: string;
  userEmail: string;
  userName: string;
  companyName?: string;
  dataToAnonymize: DataItem[];
  dataToDelete: DataItem[];
  dataToPreserve: DataItem[];
  totalRecords: number;
  confirmationToken: string;
  expiresAt: string;
}

interface DeletionResult {
  success: boolean;
  userId: string;
  userEmail: string;
  anonymizedRecords: { category: string; table: string; count: number }[];
  deletedRecords: { category: string; table: string; count: number }[];
  preservedRecords: { category: string; table: string; count: number }[];
  totalProcessed: number;
  processedAt: string;
}

type Step = 'search' | 'preview' | 'confirm' | 'complete';

export default function UserDeletionPage() {
  const [step, setStep] = useState<Step>('search');
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<DeletionPreview | null>(null);
  const [result, setResult] = useState<DeletionResult | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleSearch = async () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/users/deletion/preview', {
        userEmail: email.trim(),
      });
      setPreview(response.data);
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.message || 'User not found or error generating preview');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToConfirm = () => {
    setEmailConfirm('');
    setAcknowledged(false);
    setStep('confirm');
  };

  const handleConfirmDelete = async () => {
    if (!preview) return;

    if (emailConfirm.toLowerCase().trim() !== preview.userEmail.toLowerCase()) {
      setError('Email does not match. Please type the exact email address.');
      return;
    }

    if (!acknowledged) {
      setError('You must acknowledge that you understand this action.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/users/deletion/confirm', {
        confirmationToken: preview.confirmationToken,
        emailConfirmation: emailConfirm.trim(),
      });
      setResult(response.data);
      setStep('complete');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process user data removal');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = () => {
    setStep('search');
    setEmail('');
    setEmailConfirm('');
    setPreview(null);
    setResult(null);
    setError('');
    setAcknowledged(false);
  };

  const formatUserType = (type: string) => {
    switch (type) {
      case 'dealer_contact':
        return 'Dealer Admin';
      case 'dealer_sales_rep':
        return 'Sales Rep';
      case 'employee':
        return 'Employee';
      default:
        return type;
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Data Removal</h1>
        <p className="text-gray-600 mt-1">
          Remove personal data while preserving business records for compliance.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">GDPR-Compliant Data Removal</h3>
            <p className="mt-1 text-sm text-blue-700">
              This process <strong>anonymizes</strong> personal data (name, email, phone) while <strong>preserving</strong> business records (orders, warranties, commissions) required for tax and legal compliance.
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {['search', 'preview', 'confirm', 'complete'].map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-yellow-500 text-white'
                    : ['search', 'preview', 'confirm', 'complete'].indexOf(step) > i
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {i + 1}
              </div>
              <span
                className={`ml-2 text-sm ${
                  step === s ? 'text-yellow-600 font-medium' : 'text-gray-500'
                }`}
              >
                {s === 'search'
                  ? 'Find User'
                  : s === 'preview'
                  ? 'Review Data'
                  : s === 'confirm'
                  ? 'Confirm'
                  : 'Complete'}
              </span>
              {i < 3 && <div className="w-16 h-0.5 bg-gray-200 mx-4" />}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Step 1: Search */}
      {step === 'search' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Step 1: Find User</h2>
          <p className="text-gray-600 mb-4">
            Enter the email address of the user requesting data removal.
          </p>
          <div className="flex gap-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Find User'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && preview && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Step 2: Review Data Processing</h2>

          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-2">User Information</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>{' '}
                <span className="font-medium">{preview.userName}</span>
              </div>
              <div>
                <span className="text-gray-500">Email:</span>{' '}
                <span className="font-medium">{preview.userEmail}</span>
              </div>
              <div>
                <span className="text-gray-500">Type:</span>{' '}
                <span className="font-medium">{formatUserType(preview.userType)}</span>
              </div>
              {preview.companyName && (
                <div>
                  <span className="text-gray-500">Company:</span>{' '}
                  <span className="font-medium">{preview.companyName}</span>
                </div>
              )}
            </div>
          </div>

          {/* Data to Anonymize */}
          {preview.dataToAnonymize.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                Personal Data to Anonymize ({preview.dataToAnonymize.reduce((s, d) => s + d.count, 0)} records)
              </h3>
              <div className="border border-yellow-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-yellow-200">
                  <thead className="bg-yellow-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-yellow-700 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-yellow-700 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-yellow-700 uppercase">Records</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-yellow-100">
                    {preview.dataToAnonymize.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.description}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Data to Delete */}
          {preview.dataToDelete.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                Data to Permanently Delete ({preview.dataToDelete.reduce((s, d) => s + d.count, 0)} records)
              </h3>
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-red-200">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-700 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-700 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-red-700 uppercase">Records</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-red-100">
                    {preview.dataToDelete.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.description}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Data to Preserve */}
          {preview.dataToPreserve.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                Business Records to Preserve ({preview.dataToPreserve.reduce((s, d) => s + d.count, 0)} records)
              </h3>
              <div className="border border-green-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-green-200">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-green-700 uppercase">Records</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-green-100">
                    {preview.dataToPreserve.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item.description}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Token Expiry Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This request expires at{' '}
              {new Date(preview.expiresAt).toLocaleString()}. After that, you'll need to start over.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <button
              onClick={handleStartOver}
              className="text-gray-600 hover:text-gray-800 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleProceedToConfirm}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Proceed to Remove Data
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 'confirm' && preview && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Step 3: Final Confirmation</h2>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-800">
              You are about to remove personal data for <strong>{preview.userName}</strong> ({preview.userEmail}).
            </p>
            <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
              <li><strong>{preview.dataToAnonymize.reduce((s, d) => s + d.count, 0)}</strong> records will have personal info anonymized</li>
              <li><strong>{preview.dataToDelete.reduce((s, d) => s + d.count, 0)}</strong> records will be permanently deleted</li>
              <li><strong>{preview.dataToPreserve.reduce((s, d) => s + d.count, 0)}</strong> business records will be preserved</li>
            </ul>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type the user's email address to confirm:{' '}
              <span className="font-mono bg-gray-100 px-2 py-1 rounded">{preview.userEmail}</span>
            </label>
            <input
              type="email"
              value={emailConfirm}
              onChange={(e) => setEmailConfirm(e.target.value)}
              placeholder="Type email to confirm"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                I understand that personal data will be <strong>anonymized</strong>, some records will be <strong>permanently deleted</strong>, and business records (orders, warranties, commissions) will be <strong>preserved</strong> for legal compliance. The user will no longer be able to log in.
              </span>
            </label>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep('preview')}
              className="text-gray-600 hover:text-gray-800 font-medium"
            >
              Go Back
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={
                loading ||
                !acknowledged ||
                emailConfirm.toLowerCase().trim() !== preview.userEmail.toLowerCase()
              }
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Remove Personal Data'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 'complete' && result && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Data Removal Complete</h2>
            <p className="text-gray-600 mt-1">
              Personal data for {result.userEmail} has been processed.
            </p>
          </div>

          {/* Processing Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Processing Summary</h3>
            <div className="text-sm text-gray-600 mb-4">
              Processed at: {new Date(result.processedAt).toLocaleString()}
            </div>

            {/* Anonymized Records */}
            {result.anonymizedRecords.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-yellow-700 mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  Anonymized
                </h4>
                <div className="border border-yellow-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-yellow-200">
                    <tbody className="bg-white divide-y divide-yellow-100">
                      {result.anonymizedRecords.map((item, i) => (
                        <tr key={i}>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.category}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{item.table}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Deleted Records */}
            {result.deletedRecords.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Deleted
                </h4>
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-red-200">
                    <tbody className="bg-white divide-y divide-red-100">
                      {result.deletedRecords.map((item, i) => (
                        <tr key={i}>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.category}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{item.table}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Preserved Records */}
            {result.preservedRecords.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Preserved (for compliance)
                </h4>
                <div className="border border-green-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-green-200">
                    <tbody className="bg-white divide-y divide-green-100">
                      {result.preservedRecords.map((item, i) => (
                        <tr key={i}>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.category}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{item.table}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total records processed:</span>
                <span className="font-semibold">{result.totalProcessed}</span>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleStartOver}
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Process Another User
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
